// Recorder.tsx — presentational mic UI on top of useRecorder (CAP-1.x)
//
// Renders the live elapsed timer + start/stop control and surfaces permission denial gracefully.
// The parent owns what happens with the resulting blob (see CaptureSheet); this component just
// drives capture and reports completion via onComplete.

import { useEffect } from 'react';
import { Mic, Square, AlertTriangle, Loader2, MapPin, MapPinOff, Car } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useRecorder, type RecorderResult } from './useRecorder';
import { useDrivingAwareGeofence } from './useDrivingAwareGeofence';

export interface RecorderProps {
  /** Fired once when a recording is stopped and a blob is ready. */
  onComplete: (result: RecorderResult) => void;
  /** Label shown above the timer (e.g. "Recording meeting" / "Voice memo"). */
  title?: string;
  /** Auto-start capture on mount (after consent has been confirmed). Default true. */
  autoStart?: boolean;
}

function fmt(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60)
    .toString()
    .padStart(2, '0');
  const s = (total % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

export function Recorder({ onComplete, title = 'Recording', autoStart = true }: RecorderProps) {
  const { status, elapsedMs, error, start, stop } = useRecorder();

  useEffect(() => {
    if (autoStart) void start();
    // start is stable (useCallback); intentionally run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStop = async () => {
    const res = await stop();
    if (res) onComplete(res);
  };

  // FEATURE 2: driving-aware geofence auto-stop. Armed only while actually recording; on stop/unmount
  // the hook disarms itself (active flips false). onAutoStop simply presses the same Stop path the user
  // would — the capture pipeline is untouched. Native-only: on web the hook reports 'unavailable' and
  // never fires, so recording behaviour is unchanged off-device.
  const {
    mode: geofenceMode,
    warnSecondsRemaining,
    cancelWarning,
    stopNow,
  } = useDrivingAwareGeofence({
    active: status === 'recording',
    onAutoStop: () => {
      void handleStop();
    },
  });

  if (status === 'error') {
    return (
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <span className="rounded-full bg-destructive/10 p-3 text-destructive">
          <AlertTriangle className="h-6 w-6" />
        </span>
        <p className="text-sm text-muted-foreground">{error ?? 'Recording is unavailable.'}</p>
        <Button type="button" variant="outline" onClick={() => void start()}>
          Try again
        </Button>
      </div>
    );
  }

  const isStarting = status === 'idle' || status === 'requesting';

  return (
    <div className="flex flex-col items-center gap-4 py-4" aria-live="polite">
      <p className="text-sm font-medium text-muted-foreground">{title}</p>

      <div
        className={`flex h-24 w-24 items-center justify-center rounded-full ${
          status === 'recording'
            ? 'animate-pulse bg-destructive/15 text-destructive'
            : 'bg-primary/10 text-primary'
        }`}
      >
        {isStarting ? <Loader2 className="h-8 w-8 animate-spin" /> : <Mic className="h-9 w-9" />}
      </div>

      <div
        className="font-mono text-3xl tabular-nums"
        aria-label={`Elapsed ${fmt(elapsedMs)}`}
      >
        {fmt(elapsedMs)}
      </div>

      {status === 'recording' ? (
        <Button type="button" variant="destructive" onClick={handleStop} className="gap-2">
          <Square className="h-4 w-4" /> Stop &amp; save
        </Button>
      ) : (
        <p className="text-xs text-muted-foreground">
          {isStarting ? 'Starting microphone…' : 'Recording finished.'}
        </p>
      )}

      {/* FEATURE 2: WARN countdown banner — shown while the geofence is about to auto-stop. Gives the
          user a visible 15-20s window to keep recording or stop immediately. */}
      {status === 'recording' && geofenceMode === 'warning' && (
        <div
          role="alertdialog"
          aria-live="assertive"
          aria-label="Auto-stop pending"
          className="w-full rounded-lg border border-amber-300/60 bg-amber-50 p-3 text-left dark:border-amber-500/30 dark:bg-amber-950/40"
        >
          <div className="flex items-start gap-2">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-300" aria-hidden="true" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                Looks like you&apos;ve left
              </p>
              <p className="text-xs text-amber-800 dark:text-amber-200">
                Auto-stopping this recording
                {warnSecondsRemaining != null ? ` in ${warnSecondsRemaining}s` : ' shortly'}.
              </p>
            </div>
          </div>
          <div className="mt-2.5 flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={cancelWarning}
              className="flex-1 border-amber-400/60 bg-transparent text-amber-900 hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-900/40"
            >
              Keep recording
            </Button>
            <Button type="button" variant="destructive" onClick={stopNow} className="flex-1 gap-1.5">
              <Square className="h-3.5 w-3.5" /> Stop now
            </Button>
          </div>
        </div>
      )}

      {/* FEATURE 2: always-visible auto-stop status indicator (hidden only during the warn banner,
          which supersedes it, and when disarmed). Honestly reflects the machine's mode so the user
          always knows whether an auto-stop can happen. */}
      {status === 'recording' && geofenceMode !== 'warning' && geofenceMode !== 'disarmed' && (
        <div
          role="status"
          aria-live="polite"
          className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${
            geofenceMode === 'driving-suppressed'
              ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200'
              : geofenceMode === 'unavailable'
                ? 'bg-muted text-muted-foreground'
                : 'bg-primary/10 text-primary'
          }`}
        >
          {geofenceMode === 'driving-suppressed' ? (
            <>
              <Car className="h-3.5 w-3.5" aria-hidden="true" />
              Driving — auto-stop paused
            </>
          ) : geofenceMode === 'unavailable' ? (
            <>
              <MapPinOff className="h-3.5 w-3.5" aria-hidden="true" />
              Auto-stop unavailable
            </>
          ) : (
            <>
              <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
              Auto-stop armed
            </>
          )}
        </div>
      )}
    </div>
  );
}
